import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import api from '../services/api';

/**
 * Reached from the link at the foot of an announcement.
 *
 * Nothing happens until the button is pressed: mail clients and security
 * scanners follow links in messages, and a link that unsubscribed on sight
 * would remove people who never asked.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const token = params.get('token') || '';

  const [done, setDone] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post('/emails/unsubscribe', { email, token, reason });
      setMessage(res.data.message);
      setDone(true);
    } catch (err: any) {
      setMessage(err.response?.data?.message || 'That link could not be used.');
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <div className="rounded-lg border border-gray-200 bg-white p-8">
        {done ? (
          <div className="text-center">
            <CheckCircle className="mx-auto mb-4 h-10 w-10 text-green-600" />
            <p className="text-gray-800">{message}</p>
          </div>
        ) : (
          <>
            <h1 className="mb-2 text-2xl font-semibold text-gray-900">Stop receiving announcements</h1>
            <p className="mb-6 text-gray-600">
              {email
                ? <>We will stop sending announcements to <strong>{email}</strong>. Confirmations for
                   things you register for will still reach you.</>
                : 'This link is missing an address, so we cannot tell whom to unsubscribe.'}
            </p>

            {email && (
              <>
                <label htmlFor="reason" className="mb-1 block text-sm font-medium text-gray-700">
                  Anything you would like to tell us? (optional)
                </label>
                <textarea
                  id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  className="mb-4 w-full rounded-md border border-gray-300 px-3 py-2"
                />
                <button
                  type="button" onClick={submit} disabled={busy}
                  className="w-full rounded-md bg-orange-600 px-4 py-3 font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {busy ? 'Please wait…' : 'Unsubscribe'}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
